// src/App.tsx
import React, { Suspense } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useSearchParams,
} from "react-router-dom";
import { useAccount, useMsal } from "@azure/msal-react";
import { initializeIcons } from "@uifabric/icons";
import { MapExplorerProvider } from "@spatio/map-explorer";
import "@spatio/map-explorer/styles.css";

import { initializeFeatures } from "utils/featureFlags";
import { registerCustomIcons } from "utils/icons";
import { usePrefetchContent } from "utils/requests";
import { useAzMapsClientId } from "utils/hooks";
import { EXPLORER_ENABLED } from "utils/constants";
import NotFound from "pages/NotFound";
import Layout from "./components/Layout";
import { RequestInterceptor } from "components/auth";
import { AUTH_ENABLED } from "utils/helpers/authConstants";
import LandingPage from "pages/Landing";
import { URLS } from "utils/constants";
import Settings from "pages/Settings";
import "styles/codefiles.css";
import JsonViewerPage from "pages/JsonViewer";
import { ToasterProvider } from "components/controls/toastNotification";
import { buildHostMapExplorerConfig } from "pages/Explore-host/host-config";
import { LazyCatalogSelector } from "pages/Explore-host/LazyCatalogSelector";

// The library-backed Explorer is the default; use `?experience=legacy` for the in-app tree.
const ExploreLegacy = React.lazy(() => import("pages/Explore"));
const ExploreLibrary = React.lazy(() => import("pages/Explore-host"));

const ExploreRoute = () => {
  const [params] = useSearchParams();
  const Component =
    params.get("experience") === "legacy" ? ExploreLegacy : ExploreLibrary;
  return <Component />;
};

const Catalog = React.lazy(() => import("./pages/Catalog2"));
const CollectionDetailPage = React.lazy(() => import("pages/CollectionDetail"));
const OperationsPage = React.lazy(() => import("pages/Operations"));

// Mounts <MapExplorerProvider> above <Routes> so library hooks
// (useCollectionConfigManifest, kit hooks) resolve on every route — not just Explore.
// azMapsClientId is empty until resolved; the map inside <MapExplorer /> gates its own init.
const AppMapExplorerProvider = ({ children }: { children: React.ReactNode }) => {
  const { instance: msalInstance, accounts } = useMsal();
  const msalAccount = useAccount(accounts[0] ?? undefined) ?? null;
  const { data: azMapsClientId } = useAzMapsClientId();
  const config = React.useMemo(
    () =>
      buildHostMapExplorerConfig({
        msalInstance,
        msalAccount,
        azMapsClientId,
        catalogSelector: LazyCatalogSelector,
      }),
    [msalInstance, msalAccount, azMapsClientId]
  );
  return <MapExplorerProvider config={config}>{children}</MapExplorerProvider>;
};

function App() {
  initializeIcons(
    "https://res.cdn.office.net/files/fabric-cdn-prod_20240129.001/assets/icons/",
    { disableWarnings: true }
  );
  registerCustomIcons();
  initializeFeatures();
  usePrefetchContent();

  const pageFallback = (
    <Layout onGrid={false} isShort={true} allowAnnouncement={false}></Layout>
  );

  const { home, explorer, collections, operations, settings, json } = URLS;

  return (
    <ToasterProvider>
      <Router basename={process.env.PUBLIC_URL}>
        <RequestInterceptor enabled={AUTH_ENABLED}>
          <AppMapExplorerProvider>
            <div>
              <Routes>
                <Route path={home} element={<LandingPage />} />
                <Route
                  path={collections}
                  element={
                    <Suspense fallback={pageFallback}>
                      <Catalog />
                    </Suspense>
                  }
                />
                <Route path={settings} element={<Settings />} />
                <Route
                  path={`/${collections}/:id`}
                  element={
                    <Suspense fallback={pageFallback}>
                      <CollectionDetailPage />
                    </Suspense>
                  }
                />
                {EXPLORER_ENABLED && (
                  <Route
                    path={explorer}
                    element={
                      <Suspense fallback={pageFallback}>
                        <ExploreRoute />
                      </Suspense>
                    }
                  />
                )}
                <Route path={json} element={<JsonViewerPage />} />
                <Route
                  path={operations}
                  element={
                    <Suspense fallback={pageFallback}>
                      <OperationsPage />
                    </Suspense>
                  }
                />
                <Route path="/404" element={<NotFound />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </div>
          </AppMapExplorerProvider>
        </RequestInterceptor>
      </Router>
    </ToasterProvider>
  );
}

export default App;
